import getStreamCDNPublicUrl from "@/libs/get-cdn-public-url";
import { postPlayerStore, PostVideoAsset } from "@/libs/stores/post-player.store";
import { Box } from "@chakra-ui/react";
import { observer } from "mobx-react-lite";
import { CSSProperties, useEffect, useRef } from "react";

interface PostVideoPlayerProps {
  asset: PostVideoAsset;
  poster?: string;
  controls?: boolean;
  muted?: boolean;
  autoplay?: boolean;
  isActive?: boolean;
  style?: CSSProperties;
  onClick?: () => void;
}

const PostVideoPlayer = observer(({
  asset,
  poster,
  controls = true,
  muted = true,
  autoplay = false,
  isActive = true,
  style,
  onClick,
}: PostVideoPlayerProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  // Only attach video element when this player is active
  useEffect(() => {
    if (!isActive) {
      // Detach if this player is no longer active
      if (videoRef.current && postPlayerStore.videoRef === videoRef.current) {
        postPlayerStore.unload();
        postPlayerStore.setVideoRef(null);
      }
      return;
    }

    const ref = videoRef.current;
    if (!ref) return;
    
    if (ref.isConnected) {
      postPlayerStore.setVideoRef(ref);
    } else {
      requestAnimationFrame(() => {
        if (videoRef.current?.isConnected && isActive) {
          postPlayerStore.setVideoRef(videoRef.current);
        }
      });
    }
    
    return () => {
      if (ref && postPlayerStore.videoRef === ref && !isActive) {
        postPlayerStore.unload();
        postPlayerStore.setVideoRef(null);
      }
    };
  }, [isActive]);

  // Only load asset when this player is active and video is attached
  useEffect(() => {
    if (!isActive || !asset) {
      return;
    }

    if (videoRef.current && postPlayerStore.isVideoAttached && postPlayerStore.videoRef === videoRef.current) {
      requestAnimationFrame(() => {
        if (videoRef.current && postPlayerStore.isVideoAttached && isActive) {
          postPlayerStore.load(asset);
        }
      });
    }
  }, [isActive, asset]);

  useEffect(() => {
    if (videoRef.current) {
      postPlayerStore.setMuted(muted);
    }
  }, [muted]);

  const posterUrl = poster || (asset?.thumbnail?.clear 
    ? getStreamCDNPublicUrl(asset.thumbnail.clear) 
    : undefined);

  return (
    <Box
      position="relative"
      width="100%"
      height="100%"
      onClick={onClick}
    >
      <video
        ref={(el) => {
          videoRef.current = el;
        }}
        crossOrigin="anonymous"
        poster={posterUrl || undefined}
        controls={controls}
        controlsList="nodownload"
        playsInline
        muted={muted}
        autoPlay={autoplay}
        onError={(e) => {
          console.error("[PostVideoPlayer] Video element error:", videoRef.current?.error);
        }}
        style={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          background: "#000",
          ...style,
        }}
      />
    </Box>
  );
});

export default PostVideoPlayer;

